// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Ramps (M1-20, PH-A18, PH-A19).
//
// A ramp is a **height field over the playfield plane**, not a separate little world. The
// ball keeps its ordinary planar position and its ordinary contact solver; what changes is
// that the floor beneath it is at the ramp's height rather than at zero, and that gravity
// acquires an in-plane component pointing down the slope.
//
// For a surface `z = h(x, y)` under a gravity with a downfield part along −Y and a normal
// part along −Z, the in-plane acceleration to first order in the slope is
//
//     a = −a_downfield · ŷ  −  a_normal · ∇h
//
// which is the whole of the ramp physics. PH-A18 and PH-A19 are consequences of it rather
// than features: a ball climbing has `∇h · v̂ > 0` and decelerates, a ball descending
// accelerates, and a ball that runs out of speed rolls back down of its own accord, because
// nothing was ever holding it up. There is no "did it make the ramp" test anywhere in the
// code — the arithmetic decides, exactly as the wood does.
//
// The `a_normal` factor is worth dwelling on. A ramp's slope competes with the component of
// gravity pressing the ball into the playfield, which is nearly nine times the downfield
// pull. That is why even a gentle-looking ramp is a real shot: a 1-in-6 gradient already
// costs the ball more than the entire length of the playfield does.

import type { Ball } from '../Ball'
import type { Collider } from '../colliders'
import { makePolyline } from '../colliders'
import type { MaterialName } from '../materials'
import type { World } from '../World'
import { Device, type DeviceState } from './Device'
import { Path, createPathQuery, type PathQuery } from '../Path'
import { EV_SWITCH_CLOSED } from '../events'
import { A_NORMAL, BALL_RADIUS, MAX_BALLS, RAMP_FLOOR_THICKNESS } from '../constants'
import { playfieldFootprint } from './footprint'

export interface RampConfig {
  readonly id: string
  /** Centreline control points, playfield-local, with height. */
  readonly spline: readonly (readonly [number, number, number])[]
  /** Layer index this ramp occupies. Must not be 0, which is the playfield. */
  readonly layer: number
  /** Layer a ball arrives from and falls back to. Default 0. */
  readonly fromLayer?: number
  /** Layer a ball leaves onto at the far end. Defaults to `fromLayer`. */
  readonly toLayer?: number
  /** Clear width between the walls, metres. */
  readonly width?: number
  /** Slowest ball that can get onto the ramp at all, m/s. */
  readonly minEntrySpeed?: number
  readonly material?: MaterialName
  readonly samplesPerSegment?: number
}

export class Ramp extends Device {
  readonly kind = 'rampSurface'
  override readonly wantsForce = true
  override readonly wantsObserve = true

  readonly path: Path
  readonly layer: number
  readonly fromLayer: number
  readonly toLayer: number
  readonly width: number

  private readonly minEntrySpeed: number
  private readonly material: MaterialName
  private readonly query: PathQuery = createPathQuery()
  /** Last known sample index per ball, so a projection searches nine samples not a hundred. */
  private readonly hint = new Int32Array(MAX_BALLS)

  /**
   * The search hints, which look like a cache and are not (M4-28a).
   *
   * `projectNear` searches **only** four samples either side of the hint, so the hint is not an
   * optimisation that a wrong value merely slows down — it is the search window. Restored as
   * zero for a ball forty samples up the ramp, the projection returns the nearest point *at the
   * mouth*, which is metres away; `rideOrLeave` reads that as "off the side of the ramp" and
   * drops the ball back onto the playfield from halfway up.
   *
   * Measured rather than argued: restoring at a step with a ball part way up NEON NOVA's Solar
   * Wind ramp diverged from the uninterrupted run **on the very next step**. That is a
   * `divergent` verdict on a chunked validation — 10 §3's "the player reached the right answer
   * by a route this build does not reproduce" — for a player who did nothing wrong.
   *
   * Found together with the habitrail's missing state, by asking why the two spline-following
   * devices were the only ones with no `ownState`.
   */
  protected override ownState(): DeviceState {
    return { hint: [...this.hint] }
  }

  protected override restoreOwn(state: DeviceState): void {
    const hint = state['hint']
    if (Array.isArray(hint)) this.hint.set(hint as number[])
  }

  constructor(config: RampConfig) {
    super(config.id)
    if (config.layer === 0) {
      throw new Error(`Ramp ${config.id}: layer 0 is the playfield; a ramp needs its own layer`)
    }
    this.path = new Path(config.spline, config.samplesPerSegment ?? 12)
    this.setOrigin(config.spline[0]![0], config.spline[0]![1])
    this.layer = config.layer
    this.fromLayer = config.fromLayer ?? 0
    this.toLayer = config.toLayer ?? this.fromLayer
    this.width = config.width ?? 0.045
    this.minEntrySpeed = config.minEntrySpeed ?? 1.2
    this.material = config.material ?? 'plastic'

    if (this.width <= 2 * BALL_RADIUS) {
      throw new Error(
        `Ramp ${config.id}: width ${this.width} m is narrower than the ball's ${2 * BALL_RADIUS} m`,
      )
    }
  }

  /** Entry mouth, on the playfield. */
  get entryX(): number {
    return this.path.xs[0]!
  }
  get entryY(): number {
    return this.path.ys[0]!
  }
  /** Exit, at the far end. */
  get exitX(): number {
    return this.path.xs[this.path.sampleCount - 1]!
  }
  get exitY(): number {
    return this.path.ys[this.path.sampleCount - 1]!
  }

  override colliders(): readonly Collider[] {
    // Both walls live on the ramp's own layer, so a ball on the playfield underneath the high
    // part passes beneath them.
    const half = this.width * 0.5
    const left = this.path.offsetPolyline(half)
    const right = this.path.offsetPolyline(-half)
    return [
      ...makePolyline(left, this.material, { device: this.index, layer: this.layer }),
      ...makePolyline(right, this.material, { device: this.index, layer: this.layer }),
      // …and where the ramp is too low to pass under, it is a solid thing on the playfield too:
      // its sides, and the underside where it rises clear. See `footprint.ts`. The mouth stays
      // open, which is how a ball gets on.
      ...playfieldFootprint(this.path, {
        half,
        clearance: 2 * BALL_RADIUS + RAMP_FLOOR_THICKNESS,
        material: this.material,
        device: this.index,
      }),
    ]
  }

  override applyForce(_world: World, ball: Ball, dt: number): void {
    if (ball.layer !== this.layer) return

    // Locate where the ball is about to *be*, not where it is.
    //
    // The contact solver runs immediately after integration, so a floor set from the
    // pre-integration position is already stale by the time anything stands on it. On a
    // 1-in-5 ramp the surface climbs 0.4 mm per step, which does not sound like much until
    // you notice that it means the ball spends every single step in free fall and lands
    // again — a thousand small inelastic impacts a second, each one bleeding tangential
    // energy through the friction solver. Measured cost: a ramp that demanded 1.98 m/s
    // where the physics says 1.48, a third too hard, and every ramp in the game
    // proportionally meaner than its author intended.
    this.locateAt(ball, ball.x + ball.vx * dt, ball.y + ball.vy * dt)
    ball.floorZ = this.query.pz

    const slope = this.path.planarSlopeAt(this.query.s)
    if (slope === 0) return

    // Direction of "uphill" in the plane is the planar tangent; the acceleration opposes it
    // in proportion to the gradient.
    const i = this.query.index
    const tx = this.path.xs[i + 1]! - this.path.xs[i]!
    const ty = this.path.ys[i + 1]! - this.path.ys[i]!
    const planar = Math.sqrt(tx * tx + ty * ty)
    if (planar < 1e-12) return

    const a = A_NORMAL * slope * dt
    ball.vx -= (tx / planar) * a
    ball.vy -= (ty / planar) * a
  }

  override observe(world: World, ball: Ball): void {
    if (ball.layer === this.layer) {
      this.rideOrLeave(world, ball)
      return
    }
    if (ball.layer !== this.fromLayer) return
    this.tryEnter(world, ball)
  }

  /** A ball on the playfield reaching the mouth with enough speed gets on. */
  private tryEnter(world: World, ball: Ball): void {
    const dx = ball.x - this.entryX
    const dy = ball.y - this.entryY
    const mouth = this.width * 0.5 + BALL_RADIUS
    if (dx * dx + dy * dy > mouth * mouth) return
    if (ball.speed() < this.minEntrySpeed) return

    // It must be heading into the ramp, not drifting across its mouth.
    const i = 0
    const tx = this.path.xs[1]! - this.path.xs[0]!
    const ty = this.path.ys[1]! - this.path.ys[0]!
    const planar = Math.sqrt(tx * tx + ty * ty)
    if (planar < 1e-12) return
    if ((ball.vx * tx + ball.vy * ty) / planar < this.minEntrySpeed * 0.5) return

    ball.layer = this.layer
    this.hint[ball.id] = i
    this.locate(ball)
    ball.floorZ = this.query.pz
    ball.z = this.query.pz + BALL_RADIUS
    if (ball.vz < 0) ball.vz = 0

    world.events.emit(EV_SWITCH_CLOSED, this.index, ball.id, world.stepIndex, ball.speed())
  }

  /** Keep a ball on the ramp glued to the surface, or hand it off at either end. */
  private rideOrLeave(world: World, ball: Ball): void {
    this.locate(ball)
    ball.floorZ = this.query.pz

    // Off the side. The mouth has to be open for a ball to get on at all, so a ball arriving
    // near the edge of the entry circle can end up on this layer while being *outside* the
    // wall it should have hit — and then nothing on this layer contains it, because the walls
    // are the only geometry here. It flies out of the cabinet, and it does it slowly and
    // undramatically at 0.8 m/s, which is why the soak found it and no test did.
    if (this.query.distance > this.width * 0.5 + BALL_RADIUS) {
      this.leave(world, ball, this.fromLayer)
      return
    }

    // Which way along the ramp is the ball actually going? Position alone is not enough:
    // a ball that has just entered is *at* the mouth, and testing position only would hand
    // it straight back to the playfield on the step after it got on.
    const along = this.alongSpeed(ball)

    if (this.query.s >= this.path.length - BALL_RADIUS && along > 0) {
      this.leave(world, ball, this.toLayer)
      return
    }
    // Rolled back to the mouth: PH-A19, and it needs no code of its own beyond noticing.
    if (this.query.s <= BALL_RADIUS && along < 0) {
      this.leave(world, ball, this.fromLayer)
    }
  }

  /** Ball speed resolved along the ramp's planar centreline. Positive is up the ramp. */
  private alongSpeed(ball: Ball): number {
    const i = this.query.index
    const tx = this.path.xs[i + 1]! - this.path.xs[i]!
    const ty = this.path.ys[i + 1]! - this.path.ys[i]!
    const planar = Math.sqrt(tx * tx + ty * ty)
    if (planar < 1e-12) return 0
    return (ball.vx * tx + ball.vy * ty) / planar
  }

  private leave(world: World, ball: Ball, layer: number): void {
    ball.layer = layer
    // Landing height is the destination layer's floor; the playfield is at zero.
    ball.floorZ = layer === 0 ? 0 : this.query.pz
    if (ball.z < ball.floorZ + BALL_RADIUS) ball.z = ball.floorZ + BALL_RADIUS
    this.hint[ball.id] = 0
    world.events.emit(EV_SWITCH_CLOSED, this.index, ball.id, world.stepIndex, ball.speed())
  }

  private locate(ball: Ball): void {
    this.locateAt(ball, ball.x, ball.y)
  }

  private locateAt(ball: Ball, x: number, y: number): void {
    this.path.projectNear(x, y, this.hint[ball.id]!, 4, this.query)
    this.hint[ball.id] = this.query.index
  }

  /** Height of the ramp surface under a planar point. For the renderer and for tests. */
  heightAt(x: number, y: number): number {
    this.path.project(x, y, this.query)
    return this.query.pz
  }

  override reset(): void {
    this.hint.fill(0)
  }
}
